import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import {
  setMapLinkClickedContactLocation,
  selectMapViewDialogOpen,
} from '@user/store/map'
import { ENTITY_TYPES } from '@sales-center/common'
import { useSelector } from 'react-redux'
import { StoreData } from '@user/store/reducers/typings'
import { logger } from '@user/utils/logger'

type GeocodeStatus = 'idle' | 'pending' | 'success' | 'error'

type AddressMapLinkProps = {
  entityId: number
  entityType?: ENTITY_TYPES
  serviceLocationId?: number
  latitude?: number | null
  longitude?: number | null
}

export function useData(props: AddressMapLinkProps) {
  const { entityId, entityType, serviceLocationId, latitude, longitude } = props
  const [addressLookup, setAddressLookup] = useState<string | null>(null)
  const [location, setLocation] = useState<H.geo.Point | null>(null)
  const [geocodeStatus, setGeocodeStatus] = useState<GeocodeStatus>('idle')
  const dispatch = useAppDispatch()
  const mapViewDialogOpen = useSelector(selectMapViewDialogOpen)

  //get the current entity details from redux store using entityId and entityType
  const entityDetails = useSelector((state: StoreData) => {
    if (entityType === ENTITY_TYPES.Lead) {
      return state?.leadsState?.detail?.id === entityId
        ? state.leadsState.detail
        : state.leadsState.entities[entityId]
    } else if (entityType === ENTITY_TYPES.Opportunity) {
      return state?.opportunitiesState?.detail?.id === entityId
        ? state.opportunitiesState.detail
        : state.opportunitiesState.entities[entityId]
    }
    return null
  })

  function showErrorMessage() {
    toast.error('Failed to find location for this address.')
  }

  // Clear address lookup when dialog closes to allow re-clicking same address
  useEffect(() => {
    if (!mapViewDialogOpen && addressLookup) {
      logger.log(
        '[AddressMapLink] Dialog closed, clearing address lookup:',
        addressLookup,
      )
      setAddressLookup(null)
      setLocation(null)
      setGeocodeStatus('idle')
    }
  }, [mapViewDialogOpen, addressLookup])

  useEffect(() => {
    if (!addressLookup) {
      logger.log('[AddressMapLink] No address to lookup')
      return
    }

    // Use provided latitude/longitude instead of geocoding
    if (
      latitude == null ||
      longitude == null ||
      latitude === 0 ||
      longitude === 0
    ) {
      logger.log('[AddressMapLink] No valid coordinates provided:', {
        latitude,
        longitude,
      })
      setGeocodeStatus('error')
      showErrorMessage()
      return
    }

    logger.log('[AddressMapLink] Using provided coordinates:', {
      latitude,
      longitude,
    })
    const location = new H.geo.Point(latitude, longitude)
    setLocation(location)
    setGeocodeStatus('success')

    logger.log('[AddressMapLink] Setting clicked contact location:', {
      lat: latitude,
      lng: longitude,
      entityId,
      entityType,
      serviceLocationId,
    })
    dispatch(
      setMapLinkClickedContactLocation({
        latitude,
        longitude,
        count: 1,
        entityId: entityId,
        entityType: entityType || ENTITY_TYPES.Lead,
        multitenant: false,
        serviceLocationId: serviceLocationId || 0,
        pinId: `${entityId}${serviceLocationId || ''}`,
        entities: [],
        assigneeId: entityDetails?.assignee?.id || undefined,
      }),
    )
  }, [
    addressLookup,
    dispatch,
    entityId,
    entityType,
    serviceLocationId,
    latitude,
    longitude,
  ])

  const clear = useCallback(() => {
    setAddressLookup(null)
    setLocation(null)
    setGeocodeStatus('idle')
  }, [])

  return {
    clear,
    geocodeStatus,
    location,
    setAddressLookup,
  }
}

export type UseData = ReturnType<typeof useData>
